// shellLayout — the docked shell's panel geometry, persisted.
//
// The build spec requires the outliner and the content dock to REMEMBER their
// size and open/closed state; the inspector remembers its width (it never
// collapses). One store rather than per-component useState because three
// components (outliner, dock, menubar's Assets dock item) read and write the same
// values, and the menubar must not re-render the panels to learn them.
//
// Module singleton + subscribe, mirroring liveTuneStore / sceneSelection. Values
// are clamped on every write so a corrupt localStorage entry (or a drag past the
// edge) can't wedge a panel off-screen — the clamp IS the schema.

const LS_KEY = 'aos-shell-layout-v1'

/** Geometry limits, from the approved design's drag handlers. */
export const LIMITS = Object.freeze({
  outlinerW: Object.freeze({ min: 150, max: 340, fallback: 230 }),
  inspectorW: Object.freeze({ min: 190, max: 460, fallback: 260 }),
  dockH: Object.freeze({ min: 90, max: 380, fallback: 210 }),
  // ENG-1029: the editor-agent chat column, rightmost. Wider floor than the
  // inspector — chat bubbles under ~240px wrap into unreadable ribbons.
  agentW: Object.freeze({ min: 240, max: 560, fallback: 320 }),
  // ENG-1046: the Generate-body-animation panel, its OWN region rather than a
  // tenant of the inspector — it is an authoring task that runs for tens of
  // seconds, not a details view of the current selection, and taking the
  // inspector over meant you could not read the properties of the very clip you
  // were modifying. Wide floor because its widest row is three number fields
  // (duration / diffusion steps / seed) side by side.
  generateW: Object.freeze({ min: 300, max: 560, fallback: 380 }),
  // The event graph's MY BLUEPRINT rail (variables, events, functions). Its
  // 230 px used to be fixed; long variable and event names truncated with no
  // way to see them.
  blueprintRailW: Object.freeze({ min: 160, max: 560, fallback: 230 }),
})

/** Width of a collapsed outliner strip / height of a collapsed dock strip. */
export const COLLAPSED_SIZE = 34

const DEFAULTS = Object.freeze({
  outlinerW: LIMITS.outlinerW.fallback,
  outlinerOpen: true,
  inspectorW: LIMITS.inspectorW.fallback,
  inspectorOpen: true,
  dockH: LIMITS.dockH.fallback,
  // Collapsed is the shipped default — "one keystroke (⌥A) or a click brings
  // the browser back at its last height, the way an IDE console drawer behaves"
  // (design 2b: "collapsed is the right default; assets on demand").
  dockOpen: false,
  agentW: LIMITS.agentW.fallback,
  // Width only — this region has no open/closed preference, because it exists
  // only while a generate session does (see LIMITS.generateW).
  generateW: LIMITS.generateW.fallback,
  blueprintRailW: LIMITS.blueprintRailW.fallback,
  // Same reasoning as the dock: the agent is on-demand chrome — a collapsed
  // strip until asked for (⌥G, the strip, or View ▸ Agent).
  agentOpen: false,
})

function clampDim(key, value) {
  const lim = LIMITS[key]
  if (!Number.isFinite(value)) return lim.fallback
  return Math.min(lim.max, Math.max(lim.min, Math.round(value)))
}

/** Coerce any candidate (persisted JSON, setState patch) into a valid state. */
export function sanitize(candidate) {
  const c = candidate && typeof candidate === 'object' ? candidate : {}
  return {
    outlinerW: clampDim('outlinerW', c.outlinerW),
    outlinerOpen: typeof c.outlinerOpen === 'boolean' ? c.outlinerOpen : DEFAULTS.outlinerOpen,
    inspectorW: clampDim('inspectorW', c.inspectorW),
    inspectorOpen: typeof c.inspectorOpen === 'boolean' ? c.inspectorOpen : DEFAULTS.inspectorOpen,
    dockH: clampDim('dockH', c.dockH),
    dockOpen: typeof c.dockOpen === 'boolean' ? c.dockOpen : DEFAULTS.dockOpen,
    agentW: clampDim('agentW', c.agentW),
    agentOpen: typeof c.agentOpen === 'boolean' ? c.agentOpen : DEFAULTS.agentOpen,
    generateW: clampDim('generateW', c.generateW),
    blueprintRailW: clampDim('blueprintRailW', c.blueprintRailW),
  }
}

function load() {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null
    return sanitize(raw ? JSON.parse(raw) : DEFAULTS)
  } catch {
    return { ...DEFAULTS }
  }
}

let state = load()
const listeners = new Set()

function persist() {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS_KEY, JSON.stringify(state))
  } catch { /* private mode — geometry just won't survive the session */ }
}

function notify() {
  for (const fn of listeners) fn(state)
}

export const shellLayout = {
  getState() { return state },

  setState(patch = {}) {
    state = sanitize({ ...state, ...patch })
    persist()
    notify()
  },

  toggleOutliner() { shellLayout.setState({ outlinerOpen: !state.outlinerOpen }) },
  toggleInspector() { shellLayout.setState({ inspectorOpen: !state.inspectorOpen }) },
  toggleDock() { shellLayout.setState({ dockOpen: !state.dockOpen }) },
  toggleAgent() { shellLayout.setState({ agentOpen: !state.agentOpen }) },

  subscribe(fn) {
    listeners.add(fn)
    fn(state)
    return () => listeners.delete(fn)
  },

  /** Test seam — reset to defaults without touching storage. */
  _reset() {
    state = { ...DEFAULTS }
    listeners.clear()
  },
}

// Debug/test seam, following the window.__* convention (CLAUDE.md): lets an
// e2e drive panel geometry directly instead of synthesising drags on 5px handles.
if (typeof window !== 'undefined') {
  window.__SHELL_LAYOUT__ = shellLayout
}

export default shellLayout
